import { useEffect, useMemo, useState } from "react";
import { Search, RefreshCw, Radar } from "lucide-react";
import { toast } from "sonner";
import SectionHeader from "../components/common/SectionHeader";
import KPI from "../components/common/KPI";
import AuditableFigure from "../components/common/AuditableFigure";
import OpportunityRow from "../components/opportunity/OpportunityRow";
import OpportunityDetail from "../components/opportunity/OpportunityDetail";
import { useOpportunities } from "../hooks/data";
import { app } from "../application";
import { getEngine } from "../config/engines";
import { fmtMoney, fmtNumber, fmtPct } from "../lib/format";
import { T } from "../lib/testids";

/**
 * Generic engine detail view. Recovery uses it; new engines will reuse it
 * by passing engineId + engine-specific copy.
 */
export default function EngineView({ engineId }) {
  const cfg = getEngine(engineId);
  const [metrics, setMetrics] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const { data: opps, loading, reload, setData } = useOpportunities({
    engineId,
    priority: filter === "ALL" ? undefined : filter.toLowerCase(),
    limit: 500,
  });

  useEffect(() => {
    app.engine(engineId).getMetrics().then(setMetrics);
  }, [engineId]);

  useEffect(() => {
    if (!selectedId && opps.length) setSelectedId(opps[0].id);
  }, [opps, selectedId]);

  const filtered = useMemo(() => {
    if (!search) return opps;
    const s = search.toLowerCase();
    return opps.filter(
      (o) =>
        o.companyName.toLowerCase().includes(s) ||
        o.contactName.toLowerCase().includes(s)
    );
  }, [opps, search]);

  const dormantEstimate = useMemo(
    () =>
      metrics
        ? {
            label: "commercial_value_estimated",
            value: metrics.dormantValue,
            confidence: 0.7,
            sampleSize: metrics.opportunitiesPending,
            errorMarginPct: 11,
            rationale:
              "Suma de valor estimado en oportunidades abiertas de esta categoría. Se recalcula con cada resultado nuevo.",
            lastRecalculatedAt: new Date().toISOString(),
          }
        : null,
    [metrics]
  );

  const onOutcomeUpdated = (updated) => {
    setData((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    app.engine(engineId).getMetrics().then(setMetrics);
  };

  return (
    <div className="ambient-bg min-h-full">
      {/* Header */}
      <div className="px-4 sm:px-8 pt-6 sm:pt-8 pb-4 sm:pb-6 border-b border-[#17223a] bg-[#0d1321]/70 backdrop-blur">
        <SectionHeader
          overline={
            <span className="inline-flex items-center gap-2">
              <Radar size={12} /> Categoría activa
            </span>
          }
          title={cfg.humanLabel}
          subtitle={cfg.question}
          right={
            <button
              onClick={reload}
              className="text-[11px] font-mono uppercase tracking-[0.15em] px-3 py-2 border border-[#1b2846] text-zinc-400 hover:border-amber-500 hover:text-amber-400 flex items-center gap-2 transition-colors"
            >
              <RefreshCw size={12} /> Recalcular
            </button>
          }
        />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-b border-[#17223a]">
        <div className="border-r border-[#17223a] p-5" data-testid={T.engineKpiMoneyDormant}>
          {dormantEstimate ? (
            <AuditableFigure estimate={dormantEstimate} size="md" tone="amber" label="Dinero dormido" />
          ) : (
            <div className="text-zinc-600 font-mono">—</div>
          )}
        </div>
        <div className="border-r border-[#17223a]">
          <KPI
            testid={T.engineKpiMoneyAwake}
            label="Dinero despertado"
            value={metrics ? fmtMoney(metrics.awakeValue.amount) : "—"}
            deltaLabel={`${metrics?.opportunitiesWon || 0} cierres`}
            tone="success"
          />
        </div>
        <div className="border-r border-[#17223a]">
          <KPI
            testid={T.engineKpiWinRate}
            label="Win rate"
            value={metrics ? fmtPct(metrics.winRate) : "—"}
            deltaLabel="Ganados / (ganados + perdidos)"
            tone="neutral"
          />
        </div>
        <div>
          <KPI
            testid={T.engineKpiVelocity}
            label="Tiempo promedio para actuar"
            value={metrics ? String(metrics.avgWindowDays) : "—"}
            unit="días"
            deltaLabel="Antes de decaer"
            tone="neutral"
          />
        </div>
      </div>

      {/* Split view */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_460px] min-h-[calc(100vh-360px)]">
        <div className="border-r border-[#17223a]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-3 border-b border-[#17223a] bg-[#0d1321]">
            <div className="flex items-center gap-3">
              <div className="font-display font-bold text-sm tracking-tight text-zinc-100">
                Clientes de esta categoría
              </div>
              <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-[0.2em]">
                {filtered.length} de {opps.length}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 sm:flex-none min-w-[140px]">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full sm:w-56 bg-[#101728] border border-[#17223a] text-base sm:text-xs text-zinc-200 pl-7 pr-2 py-1.5 focus:outline-none focus:border-amber-500"
                />
              </div>
              <div className="flex border border-[#17223a]">
                {["ALL", "HIGH", "MEDIUM", "LOW"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setFilter(p)}
                    className={`text-[10px] font-mono uppercase tracking-[0.15em] px-2.5 py-1.5 transition-colors ${
                      filter === p
                        ? "bg-amber-500 text-black"
                        : "text-zinc-400 hover:text-zinc-100 hover:bg-[#131b30]"
                    }`}
                  >
                    {p === "ALL" ? "Todas" : p === "HIGH" ? "Alta" : p === "MEDIUM" ? "Media" : "Baja"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-mono border-b border-[#17223a]">
                  <th className="px-6 py-2.5 font-normal">Valor estimado</th>
                  <th className="px-4 py-2.5 font-normal w-36">Tiempo para actuar</th>
                  <th className="px-4 py-2.5 font-normal">Cuenta</th>
                  <th className="px-4 py-2.5 font-normal w-20">Prioridad</th>
                  <th className="px-4 py-2.5 font-normal">Respuesta</th>
                  <th className="px-4 py-2.5 font-normal">Qué encontramos</th>
                  <th className="px-6 py-2.5 font-normal">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-zinc-500 font-mono text-[11px]">
                      Cargando cartera...
                    </td>
                  </tr>
                )}
                {!loading &&
                  filtered.map((o) => (
                    <OpportunityRow
                      key={o.id}
                      opp={o}
                      selected={selectedId === o.id}
                      onClick={() => setSelectedId(o.id)}
                      testid={T.engineOpportunityRow(o.id)}
                    />
                  ))}
                {!loading && !filtered.length && (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-zinc-500 font-mono text-[11px]">
                      Sin oportunidades con los filtros aplicados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-[#0d1321]">
          <OpportunityDetail opportunityId={selectedId} onUpdated={onOutcomeUpdated} />
        </div>
      </div>
    </div>
  );
}
